import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DateInputContext from '../../contexts/DateInputContext';
import DatePickerTriggerContext from '../../contexts/DatePickerTriggerContext';
import moment from 'moment';
import _ from 'lodash';

import Buttons from './Buttons';
import InlineDialog from '../InlineDialog';
import RelativeInput from './RelativeInput';
import CalendarInput from './CalendarInput';
import {
  CURRENT_QUARTER,
  LAST_MONTH,
  NEXT_WEEK,
  YEAR_TO_DATE,
  YESTERDAY,
} from './constants';
import useDateScope from '../../hooks/useDateScope';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import PopupContext from '../../contexts/PopupContext';
import WorkSpaceContext from '../../contexts/WorkSpaceContext';
import CardContext from '../../contexts/CardContext';
import CanvasContext from '../../contexts/CanvasContext';
import PeriodSelector from '../Board/PeriodSelector';
import WallboardContext from '../../contexts/WallboardContext';
import { V3SelectedAppContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import NavSectionContext from '../../navigation/Authenticated/NavSectionContext';

export const useLeftText = () => {
  const { relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);

  if (relativeDateRange) {
    if (_.isEqual(relativeDateRange, YESTERDAY)) {
      return 'Yesterday';
    } else if (_.isEqual(relativeDateRange, LAST_MONTH)) {
      return 'Last Month';
    } else if (_.isEqual(relativeDateRange, YEAR_TO_DATE)) {
      return 'Year to Date';
    } else if (_.isEqual(relativeDateRange, CURRENT_QUARTER)) {
      return 'Current Quarter';
    } else if (_.isEqual(relativeDateRange, NEXT_WEEK)) {
      return 'Next Week';
    } else {
      return 'Rolling';
    }
  }

  if (advancedRelativeDateRange) {
    return 'Rolling';
  }

  return 'Custom';
};

export const useRightText = () => {
  const { relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  const dateScope = useDateScope({});

  const isWeeklyFilter =
    relativeDateRange?.interval === 'week' ||
    advancedRelativeDateRange?.from.interval === 'week' ||
    advancedRelativeDateRange?.to.interval === 'week';

  if (isWeeklyFilter) {
    // Weeks are labelled by their ending day. For most weekly filters
    // dateScope.startDate is the start of the first week, so its week-ending
    // date is 6 days later. Exception: advanced ranges where the "from" side is
    // anchored to the end of the period (either explicit config.from='end' or
    // implicit when from.type==='next' without a config) already have an
    // end-of-week date. This week-ending representation is used ONLY to detect a
    // single-week range, which collapses to a "W/E" label.
    const startIsAlreadyEndOfWeek = (() => {
      if (!advancedRelativeDateRange) return false;
      if (!advancedRelativeDateRange.config) {
        return advancedRelativeDateRange.from.type === 'next';
      }
      return advancedRelativeDateRange.config.from === 'end';
    })();
    const weekEndingStartText = startIsAlreadyEndOfWeek
      ? moment(dateScope.startDate).format('MM/DD')
      : moment(dateScope.startDate).add(6, 'd').format('MM/DD');
    const endText = moment(dateScope.endDate).format('MM/DD');
    if (weekEndingStartText === endText) {
      return `W/E ${endText}`;
    }
    // For a multi-week range (simple or advanced), show the real calendar start
    // so the label matches the date picker, instead of the week-ending of the
    // first week (ENG-783).
    const startText = moment(dateScope.startDate).format('MM/DD');
    return `${startText} - ${endText}`;
  }

  const startText = moment(dateScope.startDate).format('MM/DD');
  const endText = moment(dateScope.endDate).format('MM/DD');

  return `${startText} - ${endText}`;
};

const DateInputContainer = ({
  isBonusPeriodMode,
  isDashboardCard,
  isReport,
  isDisabled,
  onDatePickerOpen,
  onDatePickerClose,
}: {
  isBonusPeriodMode?: boolean;
  isDashboardCard?: boolean;
  isReport?: boolean;
  isDisabled?: boolean;
  onDatePickerOpen?: () => void;
  onDatePickerClose?: () => void;
}) => {
  const [selectedSide, setSelectedSide] = useState<
    'left' | 'right' | undefined
  >();
  const { workSpace } = useContext(WorkSpaceContext);
  const { isWallboard } = useContext(WallboardContext);
  const { isOpen: isPopupReportOpen } = useContext(PopupContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { i, w } = useContext(CardContext);
  const { setCardOnTopI } = useContext(CanvasContext);
  const scheduleUpdateRef = useRef<() => void>();

  const { registerOpenCallback } = useContext(DatePickerTriggerContext);

  const leftText = useLeftText();
  const rightText = useRightText();

  const updateDialogLayout = useCallback(() => {
    setTimeout(() => {
      if (scheduleUpdateRef.current) {
        scheduleUpdateRef.current();
      }
    }, 50);
  }, []);

  const onClosed = useCallback(() => {
    setSelectedSide(undefined);
    setCardOnTopI(undefined);
    if (onDatePickerClose) {
      onDatePickerClose();
    }
  }, [onDatePickerClose, setCardOnTopI]);

  const onLeftClicked = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (selectedSide === 'left') {
      setSelectedSide(undefined);
      onClosed();
      return;
    }

    setSelectedSide(undefined);
    setSelectedSide('left');
    updateDialogLayout();
    setCardOnTopI(i);
    if (onDatePickerOpen) {
      onDatePickerOpen();
    }
  }, [
    i,
    isDisabled,
    onClosed,
    onDatePickerOpen,
    selectedSide,
    setCardOnTopI,
    updateDialogLayout,
  ]);

  const onRightClicked = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (selectedSide === 'right') {
      setSelectedSide(undefined);
      onClosed();
      return;
    }

    setSelectedSide(undefined);
    setSelectedSide('right');
    updateDialogLayout();
    setCardOnTopI(i);
    if (onDatePickerOpen) {
      onDatePickerOpen();
    }
  }, [
    i,
    isDisabled,
    onClosed,
    onDatePickerOpen,
    selectedSide,
    setCardOnTopI,
    updateDialogLayout,
  ]);

  useEffect(() => {
    registerOpenCallback(onLeftClicked);
  }, [onLeftClicked, registerOpenCallback]);

  useEffect(() => {
    if (selectedSide) {
      if (isDashboardCard) {
        trackEvent('Dashboard Card - Opened Date Filter', {
          isWallboardSlide: isWallboard ? 'true' : 'false',
        });
      } else if (isReport) {
        if (isPopupReportOpen) {
          trackEvent('Popup Report - Opened Date Filter');
        } else {
          trackEvent('Report - Opened Date Filter');
        }
      }
    }
  }, [
    isDashboardCard,
    isPopupReportOpen,
    isReport,
    isWallboard,
    selectedSide,
    trackEvent,
  ]);

  if (isBonusPeriodMode) {
    if (workSpace) {
      return null;
    } else {
      return (
        <div>
          <PeriodSelector
            isWorkSpaceTopBar={false}
            isBonusPeriodMode
            onDatePickerOpen={onDatePickerOpen}
            onDatePickerClose={onDatePickerClose}
          />
        </div>
      );
    }
  } else if (workSpace && workSpace.campaignType === 'driverBonus') {
    return null;
  } else {
    return (
      <InlineDialog
        placement={'bottom-start'}
        content={
          selectedSide === 'left' ? (
            <RelativeInput updateDialogLayout={updateDialogLayout} />
          ) : (
            <CalendarInput onClosed={onClosed} />
          )
        }
        isOpen={!!selectedSide}
        onClose={onClosed}
      >
        <Buttons
          isOpen={!!selectedSide}
          isDisabled={isDisabled}
          leftText={leftText}
          isLeftSelected={selectedSide === 'left'}
          onLeftClicked={onLeftClicked}
          rightText={rightText}
          isRightSelected={selectedSide === 'right'}
          onRightClicked={onRightClicked}
          isNarrowCard={w ? w <= 3 : false}
          isDashboardCard={w !== undefined}
        />
      </InlineDialog>
    );
  }
};

// In a bonus app's period-filtered section, the period sets the range
export const useIsDateInputDisabled = (): boolean => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const { navSection } = useContext(NavSectionContext);

  const isPeriodFilterEnabled = useMemo(() => {
    if (!navSection || !navSection.sectionSettings) {
      return false;
    }

    return navSection.sectionSettings.isPeriodFilteredSection;
  }, [navSection]);

  return useMemo(() => {
    return (
      !!selectedProductApp &&
      appTypeCheckers.isBonusApp(selectedProductApp) &&
      !!isPeriodFilterEnabled
    );
  }, [isPeriodFilterEnabled, selectedProductApp]);
};

const Gate = ({
  isBonusPeriodMode,
  isDashboardCard,
  isReport,
  onDatePickerOpen,
  onDatePickerClose,
}: {
  isBonusPeriodMode?: boolean;
  isDashboardCard?: boolean;
  isReport?: boolean;
  onDatePickerOpen?: () => void;
  onDatePickerClose?: () => void;
}) => {
  const isDisabled = useIsDateInputDisabled();

  if (isDisabled && !isDashboardCard) {
    return null;
  }

  return (
    <DateInputContainer
      isDisabled={isDisabled}
      isBonusPeriodMode={isBonusPeriodMode}
      isDashboardCard={isDashboardCard}
      isReport={isReport}
      onDatePickerOpen={onDatePickerOpen}
      onDatePickerClose={onDatePickerClose}
    />
  );
};

export default Gate;
